/**
 * Fait : l'écran où l'on choisit un nouveau mot de passe, ouvert par le lien
 *   reçu par courriel. Le jeton vient du FRAGMENT de l'adresse — il n'est donc
 *   envoyé à aucun serveur intermédiaire — et ne quitte cet écran que dans le
 *   corps de la requête qui pose le mot de passe.
 *
 * Ne fait pas : il n'affiche jamais le jeton, ne dit pas à qui appartient le
 *   lien, et ne distingue pas un lien expiré d'un lien inconnu — le serveur
 *   rend un seul et même refus, et l'écran le répète tel quel.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { ChampMotDePasse } from '../gabarits/ChampMotDePasse.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import { poseLeNouveauMotDePasse } from './api.ts';

/** L'aide de l'écran, sortie du JSX pour tenir dans la largeur du code. */
const AIDE =
  'Ce lien ne sert qu’une fois. Une fois le mot de passe changé, toutes vos ' +
  'connexions en cours sont fermées.';

export function PageNouveauMotDePasse({ jeton }: { readonly jeton: string }) {
  const [motDePasse, setMotDePasse] = useState('');
  const [fait, setFait] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function enregistre(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setEnCours(true);
    setEchec(null);

    const issue = await poseLeNouveauMotDePasse(jeton, motDePasse);
    setEnCours(false);

    if (issue.bon) {
      setFait(true);
      setMotDePasse('');
      return;
    }

    setEchec(issue.erreur.message);
  }

  return (
    <div
      className="empilement"
      data-etat={fait ? 'nominal' : 'vide'}
      data-ecran="nouveau-mot-de-passe"
    >
      <Partie
        etiquette="Retrouver son accès"
        nature="gens"
        titre="Choisir un nouveau mot de passe"
        niveauDeTitre={2}
        aide={AIDE}
        data-role="partie-nouveau-mot-de-passe"
      >
        {fait ? (
          <Annonce urgence="urgent">
            <span data-role="mot-de-passe-pose">
              C’est fait. Vous pouvez maintenant entrer avec votre nouveau mot de passe.
            </span>
          </Annonce>
        ) : (
          <>
            {echec !== null && (
              <Annonce urgence="urgent" nature="refus">
                {echec}
              </Annonce>
            )}

            <form
              onSubmit={(evenement) => void enregistre(evenement)}
              data-role="nouveau-mot-de-passe"
            >
              <ChampMotDePasse
                libelle="Nouveau mot de passe"
                name="nouveau-mot-de-passe"
                value={motDePasse}
                onChange={setMotDePasse}
              />
              <button type="submit" data-role="principal" disabled={enCours || motDePasse === ''}>
                {enCours ? 'Enregistrement…' : 'Enregistrer ce mot de passe'}
              </button>
            </form>
          </>
        )}

        <p>
          <a href="#/" data-role="vers-la-connexion">
            Revenir à l’écran d’entrée
          </a>
        </p>
      </Partie>
    </div>
  );
}
